/**
 * Grid column layouts — which columns a grid shows, and in what order.
 *
 * <p>
 * Three layers, each over the one before: the columns as the screen's code
 * defines them, the organisation default, then the user's own layout. Saved
 * layouts name columns by id, never by position, so:
 * </p>
 * <ul>
 *   <li>a column added in a later release isn't mentioned by any saved layout
 *       and keeps the previous layer's state — in the end its own default —
 *       placed after the column it follows in the code;</li>
 *   <li>a column removed from the code is simply ignored;</li>
 *   <li>a locked column (e.g. the order number) is always shown, first.</li>
 * </ul>
 * <p>
 * A width travels with its column through the layers: a column dragged wider
 * keeps that width; with none, the grid sizes it to its content.
 * </p>
 * <p>
 * A user's saved layout is a snapshot of their whole grid: later changes to
 * the organisation default don't reach columns they already arranged, until
 * they reset. A layout is a starting point, never a permission.
 * </p>
 */

/** A column as the screen's code defines it. */
export interface GridColumnDef {
  /** Stable id stored in layouts — never rename without mapping the old one. */
  id: string;
  /** What the Columns chooser lists. */
  label: string;
  /** Shown when no saved layout says otherwise. Defaults to true. */
  defaultVisible?: boolean;
  /** Always shown and always first; can't be hidden or moved. */
  locked?: boolean;
}

/** A column after every layer has been applied. */
export interface ResolvedGridColumn {
  id: string;
  label: string;
  visible: boolean;
  locked: boolean;
  /** Added since the layout in force was saved — the chooser tags it "New". */
  isNew: boolean;
  /** Pixels, when someone has sized it; otherwise it fits its content. */
  width?: number;
}

/** Narrowest and widest a column can be dragged (the server accepts 20–2000). */
export const GRID_COLUMN_MIN_WIDTH = 40;
export const GRID_COLUMN_MAX_WIDTH = 1000;

/** The stored document (Directory normalises it to exactly this). */
export interface StoredGridLayout {
  v: 1;
  columns: { id: string; visible: boolean; width?: number }[];
}

type Entry = { id: string; visible: boolean; width?: number };

/** A stored layout, or null when there is none or it's a version this code doesn't read. */
export function parseGridLayout(json: string | null | undefined): StoredGridLayout | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Partial<StoredGridLayout>;
    if (parsed?.v !== 1 || !Array.isArray(parsed.columns)) return null;
    return {
      v: 1,
      columns: parsed.columns
        .filter(
          (c): c is { id: string; visible: boolean; width?: number } =>
            !!c && typeof c.id === 'string' && typeof c.visible === 'boolean',
        )
        .map((c) => ({
          id: c.id,
          visible: c.visible,
          ...(typeof c.width === 'number' && c.width > 0 ? { width: c.width } : {}),
        })),
    };
  } catch {
    return null;
  }
}

/** Lay one saved layer over the columns so far. */
function applyLayer(
  current: Entry[],
  layer: StoredGridLayout | null,
  codeOrder: string[],
): Entry[] {
  if (!layer) return current;
  const known = new Set(current.map((c) => c.id));
  const seen = new Set<string>();
  const result: Entry[] = [];
  for (const c of layer.columns) {
    if (!known.has(c.id) || seen.has(c.id)) continue;
    seen.add(c.id);
    // A layer is a snapshot: a column it mentions without a width fits its content.
    result.push({ id: c.id, visible: c.visible, width: c.width });
  }
  // Columns this layer doesn't mention keep their state, placed after the
  // nearest column before them in the code that is already placed.
  for (const c of current) {
    if (seen.has(c.id)) continue;
    let at = 0;
    for (let i = codeOrder.indexOf(c.id) - 1; i >= 0; i--) {
      const placed = result.findIndex((r) => r.id === codeOrder[i]);
      if (placed >= 0) {
        at = placed + 1;
        break;
      }
    }
    result.splice(at, 0, { ...c });
    seen.add(c.id);
  }
  return result;
}

/**
 * The grid's columns in display order, visible or not, from the code's
 * definitions and the two saved layers (either may be null).
 */
export function resolveGridColumns(
  defs: GridColumnDef[],
  organisationLayoutJson: string | null | undefined,
  userLayoutJson: string | null | undefined,
): ResolvedGridColumn[] {
  const codeOrder = defs.map((d) => d.id);
  const byId = new Map(defs.map((d) => [d.id, d]));
  const org = parseGridLayout(organisationLayoutJson);
  const user = parseGridLayout(userLayoutJson);

  let entries: Entry[] = defs.map((d) => ({ id: d.id, visible: d.defaultVisible ?? true }));
  entries = applyLayer(entries, org, codeOrder);
  entries = applyLayer(entries, user, codeOrder);

  // "New" = not in the layout in force (the user's, else the organisation's).
  const inForce = user ?? org;
  const mentioned = new Set(inForce?.columns.map((c) => c.id) ?? []);

  const resolved = entries.map((e) => {
    const def = byId.get(e.id)!;
    const locked = !!def.locked;
    return {
      id: e.id,
      label: def.label,
      visible: locked || e.visible,
      locked,
      isNew: !locked && !!inForce && !mentioned.has(e.id),
      ...(e.width ? { width: e.width } : {}),
    };
  });
  // Locked columns lead, in code order.
  return [...resolved.filter((c) => c.locked), ...resolved.filter((c) => !c.locked)];
}

/** The document to store for a grid laid out like this. */
export function gridLayoutJson(
  columns: { id: string; visible: boolean; width?: number }[],
): string {
  const layout: StoredGridLayout = {
    v: 1,
    columns: columns.map((c) => ({
      id: c.id,
      visible: c.visible,
      ...(c.width ? { width: clampWidth(c.width) } : {}),
    })),
  };
  return JSON.stringify(layout);
}

/** A dragged width, whole pixels, within the allowed range. */
export function clampWidth(width: number): number {
  return Math.round(Math.min(GRID_COLUMN_MAX_WIDTH, Math.max(GRID_COLUMN_MIN_WIDTH, width)));
}
